import {ellipsis} from 'polished';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../libs/shared/helpers/src/internationalization/react';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsisAfterNumberOfLines} from '../../../../../../libs/shared/ui/src/styles/mixins';

/*
 * Props.
 */

export interface LensesWorkspaceIngredientsTooltipProps {
  lensName: string;
  teamName?: string;
  teamInboxes: ReadonlyArray<string>;
  teamTags: ReadonlyArray<string>;
  assignees: ReadonlyArray<string>;
}

/*
 * Styles.
 */

const StyledTooltipContainerDiv = styled.div`
  display: grid;
  grid-auto-flow: row;
  grid-auto-rows: auto;
  grid-row-gap: 5px;
  padding: 5px 5px 4px 4px;
  overflow: hidden;
`;

const StyledTooltipHeaderDiv = styled.div`
  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.medium};
  color: ${(p) => p.theme.greys.white};
  display: flex;
  overflow: hidden;

  > span {
    flex-shrink: 1;
    ${ellipsis()};
  }
`;

const StyledHandlesDiv = styled.div`
  font-size: ${fontSizes.verySmall};
  color: ${(p) => p.theme.alphas.white50};
  ${ellipsisAfterNumberOfLines(2)}
`;

const StyledNameDiv = styled.div`
  white-space: pre;
  &::after {
    content: ': ';
  }
`;

const StyledHandleDiv = styled.div`
  color: ${(p) => p.theme.greys.white};
`;

const StyledContainerDiv = styled.div`
  ${StyledNameDiv} {
    float: left;
  }
`;

/*
 * Component.
 */

export const LensesWorkspaceIngredientsTooltip: FC<LensesWorkspaceIngredientsTooltipProps> = (props) => {
  const {teamInboxes, teamTags, assignees} = props;

  return (
    <StyledTooltipContainerDiv>
      <StyledTooltipHeaderDiv>
        <span>{props.lensName}</span>
      </StyledTooltipHeaderDiv>
      {props.teamName && (
        <StyledHandlesDiv>
          <StyledContainerDiv>
            <StyledNameDiv>
              <Trans id="lenses-workspace-tooltip-team">Team</Trans>
            </StyledNameDiv>
            <StyledHandleDiv>{props.teamName}</StyledHandleDiv>
          </StyledContainerDiv>
        </StyledHandlesDiv>
      )}
      {teamInboxes.length > 0 && (
        <StyledHandlesDiv>
          <StyledContainerDiv>
            <StyledNameDiv>
              <Trans id="lenses-workspace-tooltip-team-inboxes">Team inboxes</Trans>
            </StyledNameDiv>
            <StyledHandleDiv>{teamInboxes.join(', ')}</StyledHandleDiv>
          </StyledContainerDiv>
        </StyledHandlesDiv>
      )}
      {teamTags.length > 0 && (
        <StyledHandlesDiv>
          <StyledContainerDiv>
            <StyledNameDiv>
              <Trans id="lenses-workspace-tooltip-team-tags">Team tags</Trans>
            </StyledNameDiv>
            <StyledHandleDiv>{teamTags.join(', ')}</StyledHandleDiv>
          </StyledContainerDiv>
        </StyledHandlesDiv>
      )}
      {assignees.length > 0 && (
        <StyledHandlesDiv>
          <StyledContainerDiv>
            <StyledNameDiv>
              <Trans id="lenses-workspace-tooltip-assignees">Assignees</Trans>
            </StyledNameDiv>
            <StyledHandleDiv>{assignees.join(', ')}</StyledHandleDiv>
          </StyledContainerDiv>
        </StyledHandlesDiv>
      )}
    </StyledTooltipContainerDiv>
  );
};
